import {
  buildGroupedRows,
  isGroupHeaderRow,
  resolveGroupedRowsLoadMoreIndex,
  type GroupHeaderRowBase,
} from '@terra/ui-design-system';
import {
  AiApplicationInterfaceTypeSchema,
  AiComponentTypeSchema,
  type AiComponentDto,
  type AiComponentListGroupByField,
  type AiComponentListGroupDto,
  type AiComponentListOrderByDto,
  type AiComponentListSegment,
  type AiComponentListSortField,
  type AiComponentsListQueryInput,
} from '@terra/shared-contracts-ai-applications';
import type { LifecycleStateFilterDto } from '@terra/shared-dto-types';
import type { ListPageTableSort } from '../../common/list-page-infinite-table.model';
import type { OpenMainContentTabInput } from '../../../store/layoutStore';

export const AI_COMPONENTS_PAGE_SIZE = 50;
export const AI_COMPONENTS_GROUP_HEADER_ID_PREFIX = '__ai_components_group__:';
export const AI_COMPONENTS_EMPTY_GROUP_KEY = '';
export const AI_COMPONENTS_ALL_TYPES_CHIP_KEY = 'ALL';

export const AI_ASSETS_TABS = ['agents', 'components', 'endpoints'] as const;

export type AiAssetsTab = (typeof AI_ASSETS_TABS)[number];

export type AiComponentType = AiComponentDto['type'];

export const buildAiComponentTabInput = (
  component: Pick<AiComponentDto, 'id' | 'name'>,
): OpenMainContentTabInput => ({
  id: `ai-component-${component.id}`,
  title: component.name,
  type: 'ai-component',
  data: { componentId: component.id },
});

export const AI_MCP_COMPONENT_TYPES = [
  'TOOL',
  'MCP_TOOL',
  'MCP_PROMPT',
  'MCP_RESOURCE',
] as const satisfies readonly AiComponentType[];

export type AiComponentInterfaceType = NonNullable<
  AiComponentDto['interfaceType']
>;

export const AI_COMPONENT_INTERFACE_TYPES: readonly AiComponentInterfaceType[] =
  AiApplicationInterfaceTypeSchema.options;

export const isAiComponentInterfaceType = (
  value: string,
): value is AiComponentInterfaceType =>
  AI_COMPONENT_INTERFACE_TYPES.some((type) => type === value);

export const AI_COMPONENT_COVERAGE_FILTERS = ['internal', 'external'] as const;

export type AiComponentsCoverageFilter =
  (typeof AI_COMPONENT_COVERAGE_FILTERS)[number];

export const isAiComponentsCoverageFilter = (
  value: string,
): value is AiComponentsCoverageFilter =>
  AI_COMPONENT_COVERAGE_FILTERS.some((coverage) => coverage === value);

export type AiComponentsFilters = {
  coverage: AiComponentsCoverageFilter | null;
  role: string | null;
  interfaceType: AiComponentInterfaceType | null;
};

export const EMPTY_AI_COMPONENTS_FILTERS: AiComponentsFilters = {
  coverage: null,
  role: null,
  interfaceType: null,
};

export const hasAnyAiComponentsFilters = (
  filters: AiComponentsFilters,
): boolean =>
  filters.coverage !== null ||
  filters.role !== null ||
  filters.interfaceType !== null;

export type AiComponentsGroupHeaderRow = GroupHeaderRowBase & {
  componentType: AiComponentType | null;
};

export type AiComponentsTableRow = AiComponentDto | AiComponentsGroupHeaderRow;

export const isAiComponentsGroupHeaderRow = (
  row: AiComponentsTableRow | null | undefined,
): row is AiComponentsGroupHeaderRow => isGroupHeaderRow(row);

export const AI_COMPONENTS_SORT_COLUMNS = [
  'name',
  'type',
  'role',
  'createdAt',
  'signals',
  'findings',
] as const satisfies readonly AiComponentListSortField[];

export type AiComponentsSortColumn =
  (typeof AI_COMPONENTS_SORT_COLUMNS)[number];

export type AiComponentsSort = ListPageTableSort<AiComponentsSortColumn>;

export const isAiComponentsSortColumn = (
  colId: string,
): colId is AiComponentsSortColumn =>
  AI_COMPONENTS_SORT_COLUMNS.some((column) => column === colId);

export const toAiComponentsOrderBy = (
  sort: AiComponentsSort,
): AiComponentListOrderByDto[] | undefined =>
  sort ? [{ field: sort.colId, order: sort.sort }] : undefined;

export type AiComponentsGroupByField = AiComponentListGroupByField;

export const AI_COMPONENTS_GROUP_BY_FIELDS: readonly AiComponentsGroupByField[] =
  ['type', 'role', 'isInternal'];

export const toAiComponentsSearchTerm = (
  search: string,
): string | undefined => {
  const normalized = search.trim();

  return normalized.length > 0 ? normalized : undefined;
};

export const resolveAiComponentQueryTypes = ({
  selectedType,
  isMcpApplication,
}: {
  selectedType: AiComponentType | null;
  isMcpApplication: boolean;
}): AiComponentType[] | undefined => {
  if (!selectedType) {
    return isMcpApplication ? [...AI_MCP_COMPONENT_TYPES] : undefined;
  }

  return isMcpApplication && selectedType === 'MCP_TOOL'
    ? ['MCP_TOOL', 'TOOL']
    : [selectedType];
};

export const buildAiComponentsListQuery = ({
  segment,
  isMcpApplication,
  selectedType,
  search,
  filters,
  sort,
  groupBy,
  lifecycleState,
}: {
  segment: AiComponentListSegment;
  isMcpApplication: boolean;
  selectedType: AiComponentType | null;
  search: string;
  filters: AiComponentsFilters;
  sort: AiComponentsSort;
  groupBy: AiComponentsGroupByField | undefined;
  lifecycleState: LifecycleStateFilterDto | undefined;
}): AiComponentsListQueryInput => ({
  segment,
  lifecycleState,
  search: toAiComponentsSearchTerm(search),
  types: resolveAiComponentQueryTypes({ selectedType, isMcpApplication }),
  isInternal:
    filters.coverage === null ? undefined : filters.coverage === 'internal',
  role: filters.role ?? undefined,
  interfaceType: filters.interfaceType ?? undefined,
  orderBy: toAiComponentsOrderBy(sort),
  groupBy,
});

export type AiComponentTypeChip = {
  key: string;
  type: AiComponentType | null;
  count: number;
};

export const resolveAiComponentTypeChips = ({
  groups,
  isMcpApplication,
}: {
  groups: readonly AiComponentListGroupDto[];
  isMcpApplication: boolean;
}): AiComponentTypeChip[] => {
  const countByType = groups.reduce((counts, group) => {
    const parsed = AiComponentTypeSchema.safeParse(group.key);

    if (!parsed.success) {
      return counts;
    }

    const chipType =
      isMcpApplication && parsed.data === 'TOOL' ? 'MCP_TOOL' : parsed.data;

    return counts.set(chipType, (counts.get(chipType) ?? 0) + group.count);
  }, new Map<AiComponentType, number>());
  const typeChips = AiComponentTypeSchema.options.flatMap(
    (type): AiComponentTypeChip[] => {
      const count = countByType.get(type);

      return count ? [{ key: type, type, count }] : [];
    },
  );
  const total = typeChips.reduce((sum, chip) => sum + chip.count, 0);

  return [
    { key: AI_COMPONENTS_ALL_TYPES_CHIP_KEY, type: null, count: total },
    ...typeChips,
  ];
};

export const applyFilteredAiComponentTypeChipCounts = ({
  chips,
  filteredChips,
}: {
  chips: readonly AiComponentTypeChip[];
  filteredChips: readonly AiComponentTypeChip[] | null;
}): AiComponentTypeChip[] =>
  chips.map((chip) =>
    filteredChips
      ? {
          ...chip,
          count:
            filteredChips.find((filtered) => filtered.type === chip.type)
              ?.count ?? 0,
        }
      : chip,
  );

export const buildAiComponentsTypeFacetQuery = ({
  segment,
  isMcpApplication,
  search,
  filters,
  lifecycleState,
}: {
  segment: AiComponentListSegment;
  isMcpApplication: boolean;
  search: string;
  filters: AiComponentsFilters;
  lifecycleState: LifecycleStateFilterDto | undefined;
}): AiComponentsListQueryInput =>
  buildAiComponentsListQuery({
    segment,
    isMcpApplication,
    selectedType: null,
    search,
    filters,
    sort: null,
    groupBy: undefined,
    lifecycleState,
  });

export const getAiComponentGroupKey = ({
  component,
  groupBy,
}: {
  component: AiComponentDto;
  groupBy: AiComponentsGroupByField;
}): string => {
  switch (groupBy) {
    case 'type':
      return component.type;
    case 'role':
      return component.role ?? AI_COMPONENTS_EMPTY_GROUP_KEY;
    case 'isInternal':
      return String(component.isInternal);
  }
};

export const buildAiComponentsTableRows = ({
  components,
  groupBy,
  groups,
  collapsedGroupKeys,
  getGroupLabel,
}: {
  components: readonly AiComponentDto[];
  groupBy: AiComponentsGroupByField | undefined;
  groups: readonly AiComponentListGroupDto[];
  collapsedGroupKeys: ReadonlySet<string>;
  getGroupLabel: (groupKey: string) => string;
}): AiComponentsTableRow[] => {
  if (!groupBy) {
    return [...components];
  }

  const countByKey = new Map<string, number>(
    groups.map((group) => [
      group.key ?? AI_COMPONENTS_EMPTY_GROUP_KEY,
      group.count,
    ]),
  );

  return buildGroupedRows<AiComponentDto, AiComponentsGroupHeaderRow>({
    items: components,
    getItemId: (component) => component.id,
    getGroupKey: (component) => getAiComponentGroupKey({ component, groupBy }),
    getGroupLabel,
    collapsedGroupKeys,
    headerIdPrefix: AI_COMPONENTS_GROUP_HEADER_ID_PREFIX,
    knownGroupKeys: [...countByKey.keys()],
    decorateHeader: (header) => {
      const componentType =
        groupBy === 'type'
          ? AiComponentTypeSchema.safeParse(header.groupKey)
          : null;

      return {
        ...header,
        count: countByKey.get(header.groupKey) ?? header.count,
        componentType: componentType?.success ? componentType.data : null,
      };
    },
  });
};

export const resolveAiComponentsLoadMoreRowIndex = ({
  rows,
  groupBy,
  collapsedGroupKeys,
}: {
  rows: readonly AiComponentsTableRow[];
  groupBy: AiComponentsGroupByField | undefined;
  collapsedGroupKeys: ReadonlySet<string>;
}): number | null =>
  groupBy
    ? resolveGroupedRowsLoadMoreIndex({
        rows,
        collapsedGroupKeys,
        hasUnseenGroups: false,
      })
    : rows.length - 1;

export const mergeUniqueById = <Item extends { id: string }>(
  items: readonly Item[],
): Item[] => [...new Map(items.map((item) => [item.id, item])).values()];
